import React, { useState } from 'react';
import { Box, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { LayoutGridItem } from 'app/layout/LayoutGrid';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { makeStyles } from 'tss-react/mui';
import { CredOneRequirementStatus } from './enum';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectPlacementHeaderData,
  selectPlacementLandsGtgDetails,
} from '../../../../../../../store/redux-store/placement-details/selectors';
import { postFinalizedByQs } from 'app/services/PlacementServices/PlacementServices';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import { Authorized } from 'oidc/userHelper';
import { selectUser } from 'oidc/user.selectors';
import { userRoles } from 'oidc/userRoles';
import { useTranslation } from 'react-i18next';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { placementDetailsAction } from '../../../../../../../store/redux-store/placement-details/slice';
import { CustomTooltip } from 'app/components/Common/Tooltips';

const complianceGrouperOptions = [
  {
    item: 'All',
    text: 'All',
    width: '56px',
    filterType: 'All',
  },
  {
    item: 'Passed',
    image: <CheckCircleOutlineIcon style={{ color: '#5FB661' }} />,
    isImage: true,
    width: '65px',
    filterType: CredOneRequirementStatus.PASSED,
  },
  {
    item: 'Missing',
    image: <ErrorOutlineIcon style={{ color: 'orange' }} />,
    isImage: true,
    width: '65px',
    filterType: CredOneRequirementStatus.MISSING,
  },
  {
    item: 'Failed',
    image: <HighlightOffIcon color={'error'} />,
    isImage: true,
    width: '65px',
    filterType: CredOneRequirementStatus.FAILED,
  },
];

const useComplianceGrouperStyles = makeStyles()(() => ({
  grouperRoot: {
    width: '65px',
    background: 'white',
    height: ' 40px',
    border: '1px solid #C3C8D1',
    borderRadius: '0px 4px 4px 0px',
    display: 'flex',
  },
  qsCheckbox: {
    marginRight: 0,
    height: 0,
    fontWeight: 500,
  },
  qsCheckboxGrid: {
    margin: '6px'
  }
}));

export const ComplianceGrouper = ({ handleComplianceFilter }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { classes } = useComplianceGrouperStyles();
  const [finalizedByQs, setFinalizedByQs] = useState<any>(false);
  const placementDetails = useSelector(selectPlacementHeaderData);
  const [showAlert, setShowAlert] = useState<any>(false);
  const user = useSelector(selectUser);
  const landsGtgDetails = useSelector(selectPlacementLandsGtgDetails);
  const isLandsGtgCompleted = landsGtgDetails?.isgtgComplete && landsGtgDetails?.isLandsComplete;

  React.useEffect(() => {
    setFinalizedByQs(placementDetails.isFinalizedByQS);
  }, [placementDetails]);
  
  /**
   * Method to display group compliance filters
   * @param filterItem
   */
  const renderComplianceFilterIcons = filterItem => {
    if (filterItem?.isImage) {
      return <Box component="div">{filterItem.image}</Box>;
    } else {
      return <Typography component="div">{filterItem?.text}</Typography>;
    }
  };

  const checkAccessPermission = () => {
    return Authorized(
      [userRoles.credentialing_Leadership, userRoles.credentialing_TeamMember],
      user.userInfo,
    );
  };
  
  const handleConfirmAction = async () => {
    var postBody = {
      placementId: placementDetails.placementId,
      isFinalizedByQS: !finalizedByQs,
      finalizedByUserId: user.userInfo?.employeeId || 8410,
    };
    const response = await postFinalizedByQs(postBody);
    setFinalizedByQs(!finalizedByQs);
    setShowAlert(false);
    if (response?.status == 200 || response?.status == 204) {
      dispatch(
        globalActions.setSnackBar({
          message: t('placement.profile.tabs.passportCredentialXGrid.confirmActionSucessMessage'),
          severity: 'success',
        }),
      );
      dispatch(placementDetailsAction.setIsFinalisedByQs(response?.data?.isFinalizedByQS));
    } else {
      dispatch(
        globalActions.setSnackBar({
          message: t('placement.profile.tabs.passportCredentialXGrid.confirmActionErrorMessage'),
          severity: 'error',
        }),
      );
    }
  };

  return (
    <LayoutGridItem container item>
      <LayoutGridItem className={classes.grouperRoot} xs={3}>
        {complianceGrouperOptions.map((filterItem, index: number) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '65px',
              borderRight: index !== complianceGrouperOptions.length - 1 ? '1px solid #C3C8D1' : 'none',
              background: filterItem.text === 'All' ? '#009AD9' : 'white',
              color: 'white',
              '&:hover': {
                cursor: 'pointer',
              },
            }}
            onClick={() => handleComplianceFilter(filterItem.filterType)}
          >
            {renderComplianceFilterIcons(filterItem)}
          </Box>
        ))}
      </LayoutGridItem>
      <LayoutGridItem className={classes.qsCheckboxGrid}>
      <CustomTooltip tooltipContent={t('placement.profile.tabs.passportCredentialXGrid.completeLandsGtgHoverText')} disabled={isLandsGtgCompleted}>
        <FormControlLabel
          style={{ marginBottom: '3px' }}
          control={
            <Checkbox
              disableFocusRipple
              disableRipple
              disableTouchRipple
              checked={finalizedByQs}
              disabled={!checkAccessPermission() || !isLandsGtgCompleted}
              onChange={event => setShowAlert(true)}
              color="primary"
            />
          }
          className={classes.qsCheckbox}
          label={
            <Typography style={{ opacity: isLandsGtgCompleted ? '1' : '0.5', marginRight: '10px' }}>
              {t('placement.profile.tabs.passportCredentialXGrid.finalizedByQS')}
            </Typography>
          }
        />
        </CustomTooltip>
      </LayoutGridItem>
      <GenericDialog
        open={showAlert}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        dialogTitleProps={{
          style: { paddingTop: '20px' },
          text: finalizedByQs
            ? t('placement.profile.tabs.passportCredentialXGrid.confirmDialog.finalizedByQsTrueMessage')
            : t('placement.profile.tabs.passportCredentialXGrid.confirmDialog.finalizedByQsFalseMessage'),
        }}
        onClose={(e, reason) => {
          if (reason === 'backdropClick') return;
          setShowAlert(false);
        }}
        dialogActions={[
          {
            text: t('placement.profile.tabs.passportCredentialXGrid.confirmDialog.cancelBtn'),
            variant: 'contained',
            color: 'tertiary',
            onClick: () => setShowAlert(false),
          },
          {
            text: t('placement.profile.tabs.passportCredentialXGrid.confirmDialog.confirmBtn'),
            variant: 'contained',

            color: 'primary',
            onClick: () => handleConfirmAction(),
          },
        ]}
      >
        <span>
          {finalizedByQs
            ? t('placement.profile.tabs.passportCredentialXGrid.finalizedByQsTrueMessage')
            : t('placement.profile.tabs.passportCredentialXGrid.finalizedByQsFalseMessage')}
        </span>
      </GenericDialog>
    </LayoutGridItem>
  );
};
